import type { AdminProductGuild } from "@ternary/contracts"
import { Alert, Button, Dialog, Field, Input } from "@ternary/ui"
import { useRef, useState } from "react"
import { flushSync } from "react-dom"
import { Link } from "react-router-dom"
import { AdminApiError, createDiscordRole } from "../api.js"

/** Discord's longest role name, and the API's. */
const MAX_ROLE_NAME = 100

type CreatedRole = AdminProductGuild["roles"][number]

/**
 * The API's two 409s for a role it cannot make (admin-api discord-role-routes.ts), each read as
 * what is wrong with the bot and where to fix it. Any other answer keeps its own words.
 */
const BOT_REFUSALS: Record<string, string> = {
  "Ternary needs Manage Roles in this server. Re-add the bot, then try again.":
    "Ternary needs Manage Roles in this server.",
  "Ternary's bot is no longer in this server. Re-add the bot, then try again.":
    "Ternary's bot is no longer in this server.",
}

/**
 * A product's name, or a typed one, as a role name: trimmed and cut to 100 UTF-16 units (the
 * API's count), never ending in half an emoji or a space.
 */
export function roleNameFor(productName: string) {
  const cut = productName.trim().slice(0, MAX_ROLE_NAME)
  return (/[\uD800-\uDBFF]$/.test(cut) ? cut.slice(0, -1) : cut).trimEnd()
}

/**
 * "Create a new role for this product" (G4): the button opens a dialog with the role's name,
 * prefilled with the product's name, and Create role. The bot makes the role at the bottom of the
 * server, below its own, so it can give it. A dialog, so the Access step keeps fitting the screen;
 * it focuses the name, and on close focus returns to the button. It mounts only while open, so a
 * closed one takes none of the page's generated ids. No form: the wizard's own form holds the
 * button, and forms do not nest, so Enter in the name creates the role instead.
 */
export function CreateRoleField({
  disabled = false,
  guildId,
  onCreated,
  productName,
}: {
  /** A read-only account: the API refuses the role, so the button is disabled, as on Discord. */
  disabled?: boolean
  guildId: string
  onCreated(role: CreatedRole): void
  productName: string
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [name, setName] = useState("")
  // What is wrong with the name: only an empty one, so only it marks the field invalid.
  const [error, setError] = useState<string>()
  // A refusal that is not about the name, such as the API's 409s (the bot lacks Manage Roles, or
  // left the server): a danger Alert above the field, which stays neutral. `bot` is what is wrong
  // with the bot, when it is one of those, so the Alert adds where to re-add it.
  const [refusal, setRefusal] = useState<{ bot?: string | undefined; message: string } | null>(null)
  const [pending, setPending] = useState(false)

  function show() {
    setName(roleNameFor(productName))
    setError(undefined)
    setRefusal(null)
    setOpen(true)
  }

  function close() {
    setOpen(false)
  }

  /** Shows what went wrong, rendered now, and hands the name back for editing. */
  function refuse(update: () => void) {
    flushSync(update)
    inputRef.current?.focus()
  }

  async function create() {
    if (pending) return
    // The same repair as the prefill: the field's 100-unit maxLength can cut a pasted name inside
    // an emoji, and Discord would get half of it.
    const roleName = roleNameFor(name)
    if (!roleName) {
      refuse(() => setError("Enter a role name."))
      return
    }
    setPending(true)
    try {
      const role = await createDiscordRole(guildId, roleName)
      onCreated(role)
      close()
    } catch (caught) {
      const api = caught instanceof AdminApiError ? caught : null
      refuse(() =>
        setRefusal({
          // Its own keys only: an answer such as "constructor" is no bot refusal.
          bot:
            api?.status === 409 && Object.hasOwn(BOT_REFUSALS, api.message)
              ? BOT_REFUSALS[api.message]
              : undefined,
          message: api?.message ?? "The role could not be created. Try again.",
        }),
      )
    } finally {
      setPending(false)
    }
  }

  return (
    <>
      <Button disabled={disabled} onClick={show} size="sm" variant="secondary">
        Create a new role for this product
      </Button>
      {open ? (
        <Dialog
          description="Ternary adds the role below its own, so it can give the role to members."
          // While the request runs nothing dismisses the dialog, so its answer always lands here.
          // The buttons are busy, not disabled, so focus never drops out of the dialog.
          footer={
            <>
              <Button
                aria-disabled={pending || undefined}
                onClick={pending ? undefined : close}
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                aria-busy={pending || undefined}
                aria-disabled={pending || undefined}
                onClick={() => void create()}
              >
                {pending ? <span aria-hidden="true" className="tny-spinner" /> : null}
                Create role
              </Button>
            </>
          }
          onClose={close}
          open
          pending={pending}
          size="sm"
          title="Create a new role"
        >
          {refusal ? (
            <Alert tone="danger">
              {refusal.bot ? (
                <>
                  {refusal.bot} Re-add the bot in{" "}
                  <Link to="/dashboard/discord">Connections → Discord</Link>.
                </>
              ) : (
                refusal.message
              )}
            </Alert>
          ) : null}
          <Field error={error} label="Role name">
            <Input
              autoComplete="off"
              data-autofocus
              maxLength={MAX_ROLE_NAME}
              onChange={(event) => {
                setName(event.currentTarget.value)
                // An edit clears what went wrong, the refusal included.
                setError(undefined)
                setRefusal(null)
              }}
              onKeyDown={(event) => {
                if (event.key !== "Enter") return
                event.preventDefault()
                void create()
              }}
              // The name being created cannot change under it, so no edit is dropped on close.
              // Read-only, not disabled, so focus stays on it.
              readOnly={pending}
              ref={inputRef}
              value={name}
            />
          </Field>
        </Dialog>
      ) : null}
    </>
  )
}
